import { StoreApi } from 'zustand';
import { ContributorFeedback, belongsToStep } from '@scale/shared/types/chat/contributorFeedback';
import {
  StepParamsForTypeWithStage,
  InteractionStepType,
  FeedbackType,
} from '@scale/shared/types/chat';
import { createContextStore } from 'react-scale/components/scalers/tasks/ThreadedInteraction/utils/store';
import _ from 'lodash';
import { constructStepChildId } from 'react-scale/components/scalers/tasks/ThreadedInteraction/hooks/useWorkerCommentsStore';

export const MAX_DISMISSED_FEEDBACK_PER_STEP = 125;

/* ---- Zustand Types ---- */

type ZustandSetType = (
  partial:
    | ContributorFeedbackStore
    | Partial<ContributorFeedbackStore>
    | ((
        state: ContributorFeedbackStore,
      ) => ContributorFeedbackStore | Partial<ContributorFeedbackStore>),
  replace?: boolean | undefined,
) => void;

type ZustandGetType = StoreApi<ContributorFeedbackStore>['getState'];

export type ContributorFeedbackStore = {
  feedback: ContributorFeedback;
  setFeedbackState: (newState: ContributorFeedback, stepTypeWithIndexKey?: string) => void;
  getFeedbackState: (stepTypeWithIndexKey?: string) => ContributorFeedback | undefined;
  setFullFeedbackState: (newState: ContributorFeedback) => void;
  prevDismissedPerStepFeedback: ContributorFeedback;
  addDismissedPerStepFeedback: (
    newState: ContributorFeedback,
    stepTypeWithIndexKey?: string,
  ) => void;
  getPrevDismissedPerStepFeedback: (
    stepTypeWithIndexKey?: string,
  ) => ContributorFeedback | undefined;
  resetAutomatedContributorFeedbackFromResponseIndex: (
    stepParams: StepParamsForTypeWithStage<InteractionStepType>[],
    responseIndex: number,
  ) => void;
};

/* ---- Private Store Methods ---- */

const _setFeedbackState = (
  set: ZustandSetType,
  newState: ContributorFeedback,
  stepTypeWithIndexKey?: string,
) => {
  set((state: ContributorFeedbackStore) => ({
    feedback: [
      ...state.feedback.filter(({ childId }) => !belongsToStep(childId, stepTypeWithIndexKey)),
      ...newState,
    ],
  }));
};

const _getFeedbackState = (get: ZustandGetType, stepTypeWithIndexKey?: string) => {
  return get().feedback.filter(({ childId }) => belongsToStep(childId, stepTypeWithIndexKey));
};

const _addDismissedPerStepFeedback = (
  set: ZustandSetType,
  newState: ContributorFeedback,
  stepTypeWithIndexKey?: string,
) => {
  set((state: ContributorFeedbackStore) => {
    const [prevDismissedCurrStepFeedback, otherPrevDismissedFeedback] = _.partition(
      state.prevDismissedPerStepFeedback,
      ({ childId }) => belongsToStep(childId, stepTypeWithIndexKey),
    );

    // We should limit how many past dismissed feedback items we actually keep in the store, in order to prevent bloat
    // and retain frontend performance in the case of many feedback iterations. At a certain point, we should just remove
    // the oldest dismissed feedback items for the step to prevent the store from potentially growing indefinitely and
    // causing performance issues. Old dismissed feedback is not as useful to keep around as new dismissed feedback anyhow.
    if (prevDismissedCurrStepFeedback.length > MAX_DISMISSED_FEEDBACK_PER_STEP) {
      return {
        prevDismissedPerStepFeedback: [
          ...newState,
          ...prevDismissedCurrStepFeedback.slice(
            0,
            prevDismissedCurrStepFeedback.length -
              Math.min(newState.length, prevDismissedCurrStepFeedback.length),
          ),
          ...otherPrevDismissedFeedback,
        ],
      };
    }
    return {
      prevDismissedPerStepFeedback: [...state.prevDismissedPerStepFeedback, ...newState],
    };
  });
};

const _getPrevDismissedPerStepFeedback = (get: ZustandGetType, stepTypeWithIndexKey?: string) => {
  return get().prevDismissedPerStepFeedback.filter(({ childId }) =>
    belongsToStep(childId, stepTypeWithIndexKey),
  );
};

const {
  Provider: ContributorFeedbackProvider,
  useContextStore: useContributorFeedbackStore,
  useImperativeApi: useContributorFeedbackImperativeApi,
} = createContextStore<ContributorFeedbackStore>((set, get) => ({
  feedback: [],
  setFeedbackState: (newState: ContributorFeedback, stepTypeWithIndexKey?: string) =>
    _setFeedbackState(set, newState, stepTypeWithIndexKey),
  getFeedbackState: (stepTypeWithIndexKey?: string) => _getFeedbackState(get, stepTypeWithIndexKey),
  setFullFeedbackState: (newState: ContributorFeedback) => set({ feedback: newState }),

  prevDismissedPerStepFeedback: [],
  addDismissedPerStepFeedback: (newState: ContributorFeedback, stepTypeWithIndexKey?: string) =>
    _addDismissedPerStepFeedback(set, newState, stepTypeWithIndexKey),
  getPrevDismissedPerStepFeedback: (stepTypeWithIndexKey?: string) =>
    _getPrevDismissedPerStepFeedback(get, stepTypeWithIndexKey),

  /**
   * We only remove automated feedback for steps after the specified response index and not
   * worker comments -- we let worker comment store deal with removing worker comments in
   * a separate function.
   */
  resetAutomatedContributorFeedbackFromResponseIndex: (
    stepParams: StepParamsForTypeWithStage<InteractionStepType>[],
    responseIndex: number,
  ) => {
    set(state => {
      // Get all steps after the specified response index
      const stepsToRemoveFeedbackFor = stepParams.slice(responseIndex + 1);

      // Get childIds from steps to remove feedback for
      const childIdsToTarget = stepsToRemoveFeedbackFor.map(step => constructStepChildId(step));

      // Filter out feedback for the steps to remove feedback for
      const [filteredFeedback, filteredPrevDismissedFeedback] = [
        state.feedback,
        state.prevDismissedPerStepFeedback,
      ].map(feedback =>
        feedback.filter(
          ({ type, childId }) =>
            !childId ||
            !childIdsToTarget.includes(childId) ||
            type === FeedbackType.HumanReviewerFeedback,
        ),
      );

      return {
        feedback: filteredFeedback,
        prevDismissedPerStepFeedback: filteredPrevDismissedFeedback,
      };
    });
  },
}));

export {
  ContributorFeedbackProvider,
  useContributorFeedbackStore,
  useContributorFeedbackImperativeApi,
};
